"use client";

import React, { useMemo } from "react";
import { useTranslations, useLocale } from "next-intl";
import { productTranslationName } from "@/lib/utils/util";
import {
	getLocalizedHengSpecDetail,
	PRODUCT_METADATA_TRANSLATIONS_KEY,
} from "@/lib/utils/productMetadata";
import { BodyText } from "@/components/BodyText";
import ImageGallerySwiper from "./ImageGallerySwiper";
import Information from "./Information";

interface ProductContentProps {
	product: any;
}

export default function ProductContent({ product }: ProductContentProps) {
	const t = useTranslations();
	const locale = useLocale();
	
	// 监听语言和 product 变化，重新计算产品名称
	const productName = useMemo(() => {
		return productTranslationName(product?.translation?.name) || productTranslationName(product?.name);
	}, [product?.translation?.name, product?.name, locale]);
	
	// 监听语言和 product 变化，重新解析描述
	const { description, descriptionJson } = useMemo(() => {
		const desc = product?.description ? JSON.parse(product?.description) : null;
		const descJson = product?.translation?.descriptionJson
		? JSON.parse(product?.translation?.descriptionJson)
		: null;
		return { description: desc, descriptionJson: descJson };
	}, [product?.description, product?.translation?.descriptionJson, locale]);

	const shortDescription = useMemo(() => {
		const descJson = descriptionJson as any;
		const desc = description as any;
		return descJson?.blocks?.[1]?.data?.text || desc?.blocks?.[1]?.data?.text;
	}, [descriptionJson, description]);

	const applicationText = useMemo(() => {
		const desc = description as any;
		return desc?.blocks?.[0]?.data?.text || null;
	}, [description]);
	
	// 根据当前语言读取产品 metadata；翻译缺失时回退英文原文。
	const hengSpecDetail = useMemo(() => {
		const localizedHengSpecDetail = getLocalizedHengSpecDetail(product?.metadata, locale);
		return localizedHengSpecDetail?.productInfo?.[0]?.description || null;
	}, [product?.metadata, locale]);
	
	// 获取规格信息（排除不需要显示的 key）
	const specifications = useMemo(() => {
		return product?.metadata?.filter((item: any) => {
			const excludeKeys = [
				'media',
				'cover_mp4',
				'threeModel',
				'threeModel_customs',
				'sortNumber',
				'hengSpecDetail',
				PRODUCT_METADATA_TRANSLATIONS_KEY,
			];
			return item.key && item.value && !excludeKeys.includes(item.key);
		}) || [];
	}, [product?.metadata, locale]);

	return (
		<div className="w-full">
			{/* 产品标题 */}
			<h1 className="text-3xl md:text-5xl font-meduim text-black">
				{productName}
			</h1>

			<div className="w-full h-[1px] bg-mainColor/80 my-10 max-md:my-5"></div>

			{/* 系列图片标题 */}
			<div className="flex items-center gap-3 mb-6">
				<div className="w-1 h-8 bg-mainColor"></div>
				<h2 className="max-md:text-lg text-3xl font-semibold text-gray-800">
					{t("product.SeriesOfPictures")}
				</h2>
			</div>

			{/* 产品信息卡片 */}
			<div className="bg-mainColor/5 p-6 mb-6">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					{/* 左侧：应用描述和规格信息 */}
					<div className="space-y-6">
						{/* 应用描述 */}
						{hengSpecDetail ? (
							<div className="">
								<div className="text-lg max-md:text-base text-gray-700 leading-relaxed whitespace-pre-line">
									{hengSpecDetail}
								</div>
							</div>
						) : null}
						
						<div className="h-[1px] bg-mainColor/80"></div>

						{/* 简述 - 显示在下方 */}
						{shortDescription && (
							<div className="mt-6">
								<BodyText
									innerHTML={shortDescription}
									className="text-gray-600 !text-base leading-relaxed"
								/>
							</div>
						)}
						

						{/* 询盘和分享按钮 */}
						<div className="mt-8 bg-white p-6"> 
							<Information product={product} />
						</div>
					</div>

					{/* 右侧：产品图片 */}
					<div className="flex items-center justify-center">
						<ImageGallerySwiper product={product} />
					</div>
				</div>
			</div>
		</div>
	);
}
